import React, { useMemo } from 'react';
import { graphql } from 'react-apollo';
import { useQuery } from '@apollo/react-hooks';
import compose from 'lodash/flowRight';
import { useLocation } from 'react-router-dom';
import CURRENT_USER from '../../../../GraphQl/Queries/CURRENT_USER';
import CAMPAIGN_ORDER_BASKET_ITEMS from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_BASKET_ITEMS';
import CAMPAIGN_ORDER_TRANSACTIONS_BASKET from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS_BASKET';
import CAMPAIGN_ORDER_BASKET_BY_DATE from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_BASKET_BY_DATE';
import moment from 'moment-timezone';
import { useTimezone } from '../../../../hooks/useTimezone';
import { resolveCampaignDateRange } from '../../../../utils/campaignDateRange';

// =============================================================================
// BasketItemsTabLoader
//
// THE RANGE IS ALWAYS APPLIED (#11758).
// The previous build only filtered when `startDate`/`endDate` were present in
// the URL, which no first load has. So the items table and the orders rail
// opened on the *whole report* while the KPI bar showed the flight window —
// two populations on one screen (264 orders / 541 qty / $21,817.20 against
// 243 / 501 / $19,813.80 on Shipwreck'd August). Exporting after picking a
// range then produced a CSV that legitimately disagreed with the table.
//
// The range now resolves here, once, for every pane: URL params when the user
// has picked something, otherwise the campaign's own window (flight start →
// today, capped at the last day that can hold data — see campaignDateRange).
// It is passed down as `dateRange` so the picker displays the range that is
// actually in force rather than falling back to its own default label.
//
// ITEMS FILTER ON THEIR OWN `date`, NOT THROUGH `transaction` (#11758).
// The old clause reached through the transaction relation. Basket items whose
// join row was never written — the report writer only linked on create, so a
// transaction that wasn't persisted yet left the item permanently orphaned —
// are invisible to any such filter while still showing in an unfiltered one.
// That is the "Hidden Gems – Khalifa Kush is on screen but not in the CSV"
// report. `date` lives on the item itself, so the filter no longer depends on
// a join that may be missing, and it is the same date `byDate` buckets on, so
// the KPI bar and the table finally count the same rows.
// =============================================================================

// Applied to the orders rail only. The rail lists transactions, which carry an
// attribution window; the items table and the KPI bar are item-scoped and the
// `byDate` rollup has no attrWindow dimension to filter on, so applying it
// there would re-open the KPI/table gap this fix closes. Previously this was
// set *inside* the date branch, so picking a range silently changed two things
// at once. Hoisted out, matching TransactionsTabLoader.
const BASKET_ATTRIBUTION_WINDOW = 30;

const BasketItemsTabLoader = ({ children, ...props }) => {
  const { campaignOrder } = props;
  const location = useLocation();
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();

  // Read pagination and filters from URL params
  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;
  const pageSize = parseInt(queryParams.get('pageSize')) || 10;
  const searchTerm = queryParams.get('searchTerm') || '';
  const startDateParam = queryParams.get('startDate');
  const endDateParam = queryParams.get('endDate');

  // Scope every basket query to the single transaction report tied to this
  // campaign order via its direct 1:1 pointer. Filtering by campaign order id
  // alone pulls rows from every report tied to the order (re-processing runs /
  // orphans), inflating quantities, totals, and the grouped order list.
  const reportId = campaignOrder?.reportDetails?.transaction?.id;

  // ---------------------------------------------------------------------------
  // 1. byDate first — it is cheap, needs no range, and its extent is what the
  //    default range is resolved against (attribution tails run past the
  //    flight end, and the default has to reach them).
  // ---------------------------------------------------------------------------
  const { data: basketItemsByDateData, loading: byDateLoading } = useQuery(
    CAMPAIGN_ORDER_BASKET_BY_DATE,
    {
      variables: { reportId },
      skip: !reportId,
    }
  );

  const basketItemsByDate = useMemo(
    () => basketItemsByDateData?.campaignOrderTransactionsBasketItemsByDates || [],
    [basketItemsByDateData]
  );

  // ---------------------------------------------------------------------------
  // 2. Resolve the range. UTC-pinned for display: flight dates are stored
  //    UTC-anchored and the charts bucket in UTC, so reading them in the
  //    viewer's zone would shift the window a day for anyone off UTC.
  // ---------------------------------------------------------------------------
  const defaultDateRange = useMemo(
    () => resolveCampaignDateRange(campaignOrder, basketItemsByDate.map((d) => d && d.date)),
    [campaignOrder, basketItemsByDate]
  );

  const dateRange = useMemo(() => {
    if (startDateParam && endDateParam) {
      const start = moment.utc(startDateParam, [moment.ISO_8601, 'YYYY-MM-DD'], false);
      const end = moment.utc(endDateParam, [moment.ISO_8601, 'YYYY-MM-DD'], false);
      if (start.isValid() && end.isValid()) {
        return [start.startOf('day'), end.startOf('day')];
      }
    }
    return defaultDateRange;
  }, [startDateParam, endDateParam, defaultDateRange]);

  // Query boundaries are timezone-aware even though the range itself is
  // UTC-pinned: the stored instants are real timestamps, so "Aug 1" means the
  // org's Aug 1. Half-open [gte, lt) rather than <= endOf('day') — no
  // millisecond edge to fall through. Mirrors TransactionsTabLoader.
  const dateBounds = useMemo(() => {
    if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) return null;

    const startKey = dateRange[0].format('YYYY-MM-DD');
    const endKey = dateRange[1].format('YYYY-MM-DD');

    const startMoment =
      effectiveTimezone === 'UTC'
        ? moment.utc(startKey).startOf('day')
        : moment.tz(startKey, effectiveTimezone).startOf('day');

    const endMoment =
      effectiveTimezone === 'UTC'
        ? moment.utc(endKey).add(1, 'day').startOf('day')
        : moment.tz(endKey, effectiveTimezone).add(1, 'day').startOf('day');

    return { gte: startMoment.toISOString(), lt: endMoment.toISOString() };
  }, [dateRange, effectiveTimezone]);

  // ---------------------------------------------------------------------------
  // 3. Where clauses. Both panes carry the range; neither can drift from it.
  // ---------------------------------------------------------------------------
  const basketItemsWhere = useMemo(() => {
    const scope = { transactionReport: { id: reportId } };
    if (!dateBounds) return scope;
    return {
      AND: [scope, { date_gte: dateBounds.gte, date_lt: dateBounds.lt }],
    };
  }, [reportId, dateBounds]);

  const transactionsBasketWhere = useMemo(() => {
    const conditions = [
      { campaignOrderTransactionReport: { id: reportId } },
      { attrWindow_lte: BASKET_ATTRIBUTION_WINDOW },
    ];

    if (dateBounds) {
      conditions.push({
        transactionDate_gte: dateBounds.gte,
        transactionDate_lt: dateBounds.lt,
      });
    }

    if (searchTerm) {
      conditions.push({ orderId_contains: searchTerm });
    }

    return { AND: conditions };
  }, [reportId, dateBounds, searchTerm]);

  // Hold the scoped queries until the range has resolved, so the first paint
  // is never the unfiltered set that #11758 was about.
  const rangeReady = !!reportId && !byDateLoading;

  const { data: basketItemsData, loading: basketItemsDataLoading } = useQuery(
    CAMPAIGN_ORDER_BASKET_ITEMS,
    {
      variables: {
        where: basketItemsWhere,
        orderBy: 'total_DESC',
      },
      skip: !rangeReady,
    }
  );

  const { data: basketItemsGroupedOrderId, loading: basketItemsGroupedLoading } = useQuery(
    CAMPAIGN_ORDER_TRANSACTIONS_BASKET,
    {
      variables: {
        where: transactionsBasketWhere,
        first: pageSize,
        skip: (Number(currentPage) - 1) * pageSize,
        orderBy: 'transactionDate_ASC',
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: !rangeReady,
    }
  );

  return React.cloneElement(children, {
    campaignOrder,
    pageSize,
    basketItemsData: basketItemsData?.campaignOrderTransactionsBasketItemsesConnection?.edges?.map(
      (o) => ({ ...o.node })
    ),
    basketItemsGroupedOrderId:
      basketItemsGroupedOrderId?.campaignOrderTransactionsWithEventsesConnection?.edges?.map(
        (o) => ({ ...o.node })
      ),
    basketItemsGroupedTotalCount: basketItemsGroupedOrderId?.full?.aggregate?.count || 0,
    currentPage,
    basketItemsDataLoading: byDateLoading || basketItemsDataLoading || basketItemsGroupedLoading,
    basketItemsByDate,
    dateRange,
    defaultDateRange,
    dateBounds,
    ...props,
  });
};

export default compose(
  graphql(CURRENT_USER, {
    props: ({ data: { currentUser }, ownProps }) => {
      return ownProps.currentUser || { currentUser };
    },
  })
)(BasketItemsTabLoader);
